import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MessageCircle, Phone, Mail, Clock, CheckCircle, AlertTriangle, Truck, Package, MapPin, Send, User, Bot, BarChart3, ArrowRight, ChevronDown } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

const TICKET_COLORS = { open: '#FFC857', in_progress: '#7A5CFF', waiting_customer: '#9CB0C7', resolved: '#3EF1C7', escalated: '#F55454' };
const PRIORITY_COLORS = { low: '#9CB0C7', medium: '#FFC857', high: '#F55454', urgent: '#F55454' };

export default function WISMOPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('lookup');
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupType, setLookupType] = useState('order_id');
  const [lookupResult, setLookupResult] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [delayed, setDelayed] = useState([]);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tRes, dRes, delRes] = await Promise.all([
        apiClient.get('/api/v1/wismo/tickets'),
        apiClient.get('/api/v1/wismo/dashboard'),
        apiClient.get('/api/v1/wismo/delayed'),
      ]);
      setTickets(tRes.data.tickets || []);
      setDashboard(dRes.data);
      setDelayed(delRes.data.orders || []);
    } catch {
      setTickets([
        { ticket_id: 'tkt_001', order_id: 'ord_a1b2c3', customer_email: 'sarah@example.com', issue_type: 'wismo', status: 'open', priority: 'medium', subject: 'Where is my order CI-7F2A9B31?', messages: [{ sender: 'customer', text: 'Hi, I placed an order 3 days ago and haven\'t received tracking yet. Can you help?', timestamp: '2026-03-06T10:30:00Z' }, { sender: 'ai_agent', text: 'I can see your order CI-7F2A9B31 is currently being picked at our warehouse. You should receive tracking within 24 hours. Current status: Picking in progress at Warehouse A.', timestamp: '2026-03-06T10:30:05Z' }], assigned_to: 'AI Agent', created_at: '2026-03-06T10:30:00Z' },
        { ticket_id: 'tkt_002', order_id: 'ord_d4e5f6', customer_email: 'mike@example.com', issue_type: 'wismo', status: 'in_progress', priority: 'high', subject: 'Split shipment - where is second package?', messages: [{ sender: 'customer', text: 'I received one package but my running shoes are missing from the order.', timestamp: '2026-03-05T14:00:00Z' }, { sender: 'csr', text: 'Your order was split into 2 shipments. The running shoes are shipping from our San Francisco store and should arrive by March 8.', timestamp: '2026-03-05T14:15:00Z' }], assigned_to: 'CSR - Jamie', created_at: '2026-03-05T14:00:00Z' },
        { ticket_id: 'tkt_003', order_id: 'ord_x9y8z7', customer_email: 'agent-alpha@openclaw.io', issue_type: 'cancel', status: 'escalated', priority: 'urgent', subject: 'Agent purchase cancellation request', messages: [{ sender: 'customer', text: 'Need to cancel order immediately - found better deal via moltbook feed.', timestamp: '2026-03-06T09:00:00Z' }], assigned_to: 'Escalation Team', created_at: '2026-03-06T09:00:00Z' },
      ]);
      setDashboard({
        open_tickets: 12, in_progress: 8, avg_resolution_hours: 4.2,
        deflection_rate: 67.3, sla_compliance: 94.1, ai_handled: 156, human_handled: 78,
        by_type: { wismo: 45, cancel: 12, modify: 8, return: 15, refund: 6, complaint: 4 },
      });
      setDelayed([
        { order_id: 'ord_delay1', customer: 'Tom Brown', status: 'shipped', days_delayed: 3, carrier: 'UPS', tracking: 'UPS-1Z999AA1012', last_scan: 'Memphis, TN', expected: 'Mar 3', issue: 'Weather delay' },
        { order_id: 'ord_delay2', customer: 'Agent-9K2M', status: 'picking', days_delayed: 2, carrier: null, tracking: null, last_scan: 'Warehouse B', expected: 'Mar 4', issue: 'Inventory shortage' },
      ]);
    }
  };

  const doLookup = async () => {
    if (!lookupQuery.trim()) return;
    try {
      const res = await apiClient.post('/api/v1/wismo/lookup', { query: lookupQuery, query_type: lookupType });
      setLookupResult(res.data);
    } catch {
      setLookupResult({
        order_id: lookupQuery || 'ord_a1b2c3',
        status: 'picking',
        status_display: 'Your order is being prepared for shipment',
        estimated_delivery: 'March 8-10, 2026',
        can_cancel: true,
        can_modify: false,
        shipments: [
          { shipping_order_id: 'so_abc123', status: 'picking', carrier: null, tracking: null, items: ['Wireless Headphones', 'USB-C Cable 3pk'], node: 'Warehouse A - Los Angeles' },
        ],
        milestones: [
          { event: 'Order Placed', timestamp: '2026-03-06T10:00:00Z', completed: true },
          { event: 'Payment Authorized', timestamp: '2026-03-06T10:00:05Z', completed: true },
          { event: 'Order Confirmed', timestamp: '2026-03-06T10:01:00Z', completed: true },
          { event: 'Allocated to Warehouse', timestamp: '2026-03-06T10:05:00Z', completed: true },
          { event: 'Picking in Progress', timestamp: '2026-03-06T11:00:00Z', completed: true },
          { event: 'Packed', timestamp: null, completed: false },
          { event: 'Shipped', timestamp: null, completed: false },
          { event: 'Delivered', timestamp: null, completed: false },
        ],
      });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">WISMO & Contact Center</h1>
            <p className="text-sm text-[#9CB0C7]">Where Is My Order • Order Support • AI Deflection</p>
          </div>
          <div className="flex items-center gap-3">
            {['lookup', 'tickets', 'delayed', 'dashboard'].map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === t ? 'bg-[#7A5CFF] text-white' : 'border border-[#22304A] text-[#9CB0C7] hover:text-white'}`}>
                {t === 'lookup' ? 'Order Lookup' : t === 'tickets' ? 'Tickets' : t === 'delayed' ? 'Delayed' : 'Dashboard'}
              </button>
            ))}
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Dashboard</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab: Order Lookup */}
        {tab === 'lookup' && (
          <div className="space-y-6">
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <div className="text-lg text-white font-medium mb-4">Order Lookup</div>
              <div className="flex gap-3">
                <select value={lookupType} onChange={e => setLookupType(e.target.value)} className="bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2.5 text-white text-sm">
                  <option value="order_id">Order ID</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="tracking">Tracking #</option>
                  <option value="confirmation">Confirmation #</option>
                </select>
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CB0C7]" />
                  <input value={lookupQuery} onChange={e => setLookupQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && doLookup()} placeholder="Enter order ID, email, or tracking number..." className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm" />
                </div>
                <button onClick={doLookup} className="px-6 py-2.5 bg-[#7A5CFF] text-white rounded-lg font-medium text-sm">Search</button>
              </div>
            </div>

            {lookupResult && (
              <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm text-[#7A5CFF] font-mono">{lookupResult.order_id}</div>
                    <div className="text-lg text-white font-medium">{lookupResult.status_display}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {lookupResult.can_cancel && <button className="px-3 py-1.5 border border-[#F55454] text-[#F55454] rounded-lg text-xs">Cancel Order</button>}
                    <span className="px-3 py-1.5 bg-[#3EF1C7]/10 text-[#3EF1C7] rounded-lg text-xs">ETA: {lookupResult.estimated_delivery}</span>
                  </div>
                </div>

                {/* Milestones */}
                <div className="mb-6">
                  <div className="text-xs text-[#9CB0C7] mb-3 font-medium">ORDER TIMELINE</div>
                  <div className="flex items-center gap-1">
                    {lookupResult.milestones?.map((m, i) => (
                      <React.Fragment key={i}>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${m.completed ? 'bg-[#3EF1C7]' : 'border-2 border-[#22304A]'}`}>
                            {m.completed && <CheckCircle className="w-4 h-4 text-[#0B1120]" />}
                          </div>
                          <div className={`text-[9px] mt-1 text-center w-16 ${m.completed ? 'text-[#3EF1C7]' : 'text-[#9CB0C7]'}`}>{m.event}</div>
                        </div>
                        {i < lookupResult.milestones.length - 1 && (
                          <div className={`flex-1 h-0.5 mb-4 ${m.completed ? 'bg-[#3EF1C7]' : 'bg-[#22304A]'}`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Shipments */}
                {lookupResult.shipments?.map((s, i) => (
                  <div key={i} className="bg-[#0B1120] rounded-lg p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-[#7A5CFF]" />
                        <span className="text-sm text-white font-mono">{s.shipping_order_id}</span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#FFC857]/10 text-[#FFC857]">{s.status}</span>
                      </div>
                      {s.tracking && <span className="text-xs text-[#3EF1C7] font-mono">{s.tracking}</span>}
                    </div>
                    <div className="text-xs text-[#9CB0C7]"><MapPin className="w-3 h-3 inline mr-1" />{s.node}</div>
                    <div className="text-xs text-[#9CB0C7] mt-1">{s.items?.join(', ')}</div>
                  </div>
                ))}

                {/* Quick Actions */}
                <div className="flex gap-3 mt-4">
                  <button className="flex-1 py-2 bg-[#7A5CFF]/10 text-[#7A5CFF] rounded-lg text-xs font-medium flex items-center justify-center gap-1"><MessageCircle className="w-3 h-3" /> Send SMS Update</button>
                  <button className="flex-1 py-2 bg-[#7A5CFF]/10 text-[#7A5CFF] rounded-lg text-xs font-medium flex items-center justify-center gap-1"><Mail className="w-3 h-3" /> Email Customer</button>
                  <button className="flex-1 py-2 bg-[#7A5CFF]/10 text-[#7A5CFF] rounded-lg text-xs font-medium flex items-center justify-center gap-1"><Phone className="w-3 h-3" /> Call Customer</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab: Tickets */}
        {tab === 'tickets' && (
          <div className="space-y-4">
            {tickets.map(t => (
              <div key={t.ticket_id} className="bg-[#0E1426] border border-[#22304A] rounded-xl overflow-hidden">
                <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedTicket(expandedTicket === t.ticket_id ? null : t.ticket_id)}>
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-mono text-[#9CB0C7]">{t.ticket_id}</span>
                    <span className="text-sm text-white">{t.subject}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${TICKET_COLORS[t.status]}15`, color: TICKET_COLORS[t.status] }}>{t.status.replace('_', ' ')}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${PRIORITY_COLORS[t.priority]}15`, color: PRIORITY_COLORS[t.priority] }}>{t.priority}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#9CB0C7]">{t.assigned_to}</span>
                    <ChevronDown className={`w-4 h-4 text-[#9CB0C7] transition ${expandedTicket === t.ticket_id ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {expandedTicket === t.ticket_id && (
                  <div className="border-t border-[#22304A] p-4">
                    <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                      {t.messages?.map((msg, i) => (
                        <div key={i} className={`flex gap-3 ${msg.sender === 'customer' ? '' : 'justify-end'}`}>
                          <div className={`max-w-[70%] rounded-lg p-3 ${msg.sender === 'customer' ? 'bg-[#0B1120]' : msg.sender === 'ai_agent' ? 'bg-[#7A5CFF]/10 border border-[#7A5CFF]/30' : 'bg-[#22304A]'}`}>
                            <div className="flex items-center gap-1 mb-1">
                              {msg.sender === 'ai_agent' ? <Bot className="w-3 h-3 text-[#7A5CFF]" /> : <User className="w-3 h-3 text-[#9CB0C7]" />}
                              <span className="text-[10px] text-[#9CB0C7]">{msg.sender === 'ai_agent' ? 'AI Agent' : msg.sender === 'csr' ? 'CSR' : 'Customer'}</span>
                            </div>
                            <div className="text-sm text-white">{msg.text}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type a reply..." className="flex-1 bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" />
                      <button className="px-4 py-2 bg-[#7A5CFF] text-white rounded-lg text-sm flex items-center gap-1"><Send className="w-3 h-3" /> Reply</button>
                      {t.status !== 'resolved' && <button className="px-4 py-2 bg-[#3EF1C7]/10 text-[#3EF1C7] rounded-lg text-sm">Resolve</button>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tab: Delayed */}
        {tab === 'delayed' && (
          <div className="space-y-4">
            <div className="bg-[#F55454]/5 border border-[#F55454]/30 rounded-xl p-4 flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-[#F55454]" />
              <span className="text-[#F55454] font-medium">{delayed.length} delayed orders need attention</span>
            </div>
            {delayed.map((d, i) => (
              <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#F55454]/10 flex items-center justify-center text-[#F55454] font-bold text-sm">+{d.days_delayed}d</div>
                  <div>
                    <div className="text-sm text-white">{d.customer} <span className="text-[#7A5CFF] font-mono text-xs ml-2">{d.order_id}</span></div>
                    <div className="text-xs text-[#9CB0C7]">{d.issue} • Last scan: {d.last_scan} • Expected: {d.expected}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 bg-[#7A5CFF]/10 text-[#7A5CFF] rounded text-xs">Create Ticket</button>
                  <button className="px-3 py-1.5 bg-[#FFC857]/10 text-[#FFC857] rounded text-xs">Send SMS</button>
                  <button className="px-3 py-1.5 bg-[#F55454]/10 text-[#F55454] rounded text-xs">Escalate</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tab: Dashboard */}
        {tab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Open Tickets', value: dashboard.open_tickets, color: '#FFC857', icon: MessageCircle },
                { label: 'AI Deflection Rate', value: `${dashboard.deflection_rate}%`, color: '#3EF1C7', icon: Bot },
                { label: 'Avg Resolution', value: `${dashboard.avg_resolution_hours}h`, color: '#7A5CFF', icon: Clock },
                { label: 'SLA Compliance', value: `${dashboard.sla_compliance}%`, color: '#3EF1C7', icon: CheckCircle },
              ].map((s, i) => (
                <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1"><s.icon className="w-4 h-4" style={{ color: s.color }} /><span className="text-xs text-[#9CB0C7]">{s.label}</span></div>
                  <div className="text-2xl font-bold text-white">{s.value}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
                <div className="text-sm text-[#9CB0C7] mb-4 font-medium">TICKETS BY TYPE</div>
                {Object.entries(dashboard.by_type).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between py-2 border-b border-[#22304A]/50">
                    <span className="text-sm text-white capitalize">{type}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-[#0B1120] rounded-full"><div className="h-full bg-[#7A5CFF] rounded-full" style={{ width: `${(count / 50) * 100}%` }} /></div>
                      <span className="text-sm text-white w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
                <div className="text-sm text-[#9CB0C7] mb-4 font-medium">AI vs HUMAN HANDLING</div>
                <div className="flex items-end gap-8 justify-center py-8">
                  <div className="text-center">
                    <Bot className="w-8 h-8 text-[#7A5CFF] mx-auto mb-2" />
                    <div className="text-3xl font-bold text-[#7A5CFF]">{dashboard.ai_handled}</div>
                    <div className="text-xs text-[#9CB0C7]">AI Handled</div>
                    <div className="text-xs text-[#3EF1C7]">{Math.round(dashboard.ai_handled / (dashboard.ai_handled + dashboard.human_handled) * 100)}%</div>
                  </div>
                  <div className="text-center">
                    <User className="w-8 h-8 text-[#FFC857] mx-auto mb-2" />
                    <div className="text-3xl font-bold text-[#FFC857]">{dashboard.human_handled}</div>
                    <div className="text-xs text-[#9CB0C7]">Human Handled</div>
                    <div className="text-xs text-[#9CB0C7]">{Math.round(dashboard.human_handled / (dashboard.ai_handled + dashboard.human_handled) * 100)}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
